/**
 * Summary resolution card for the Text Notes content config. Edits the summary
 * field (enabled, field name, source columns) and its ordered list of rules.
 * Each rule produces a summary value when its `when` conditions match — e.g. the
 * `calling_resolved` rule (call_required is any of [yes, archive] AND
 * calling_status is any of [Resolved]). The `when` conditions are edited with the
 * shared QueryBuilder, matching the run_when / preconditions editors.
 */
import { ToggleSwitch } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo } from "react";
import { Button, TextInput } from "risa-oasis-ui_v2";
import MultiSelectWithTag from "../../../../../components/multiSelectWithTag/MultiSelectWithTag";
import QueryBuilder from "../../../../../components/queryBuilder/QueryBuilder";
import { useCmmInputUniqueValuesContext } from "../../../../../context/cmmInputUniqueValuesContext";
import { QueryGroup } from "../../../../../data-model/queryBuilder";
import { Delete } from "../../../../../svg/delete";
import { buildDeliveryStepConditionConfig } from "../shared/deliveryStepConditionConfig";
import {
  buildSummaryWhenConfig,
  queryValueToWhen,
} from "./summaryWhenConversion";
import { SummaryRuleRow } from "./textNotesContentUtils";

interface SummaryResolutionCardProps {
  resetKey: number;
  enabled: boolean;
  fieldName: string;
  sourceColumns: string[];
  rules: SummaryRuleRow[];
  onEnabledChange: (value: boolean) => void;
  onFieldNameChange: (value: string) => void;
  onSourceColumnsChange: (values: string[]) => void;
  onAddRule: () => void;
  onRemoveRule: (id: string) => void;
  onRuleChange: (id: string, changes: Partial<SummaryRuleRow>) => void;
  onWhenChange: (id: string, when: QueryGroup) => void;
}

/**
 * Presentational editor for summary resolution settings and per-rule when-builders.
 *
 * @param props - Controlled summary resolution state and change handlers
 */
const SummaryResolutionCard = (props: SummaryResolutionCardProps) => {
  const {
    resetKey,
    enabled,
    fieldName,
    sourceColumns,
    rules,
    onEnabledChange,
    onFieldNameChange,
    onSourceColumnsChange,
    onAddRule,
    onRemoveRule,
    onRuleChange,
    onWhenChange,
  } = props;

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();

  const sourceColumnOptions = useMemo(() => {
    const fromCmm = (cmmInputUniqueValues?.columns ?? []).map((col) => ({
      id: col.column_name,
      label: col.column_name,
    }));
    const existingIds = new Set(fromCmm.map((option) => option.id));
    const preserved = sourceColumns
      .map((column) => column.trim())
      .filter((column) => column && !existingIds.has(column))
      .map((column) => ({ id: column, label: column }));
    return [...fromCmm, ...preserved];
  }, [cmmInputUniqueValues, sourceColumns]);

  // Selectable columns for the when-builder: delivery-step + condition-list fields,
  // source columns, and any column already referenced by a rule.
  const whenConfig = useMemo(() => {
    const columns = new Set(sourceColumns.map((c) => c.trim()).filter(Boolean));
    rules.forEach((rule) =>
      Object.keys(queryValueToWhen(rule.when)).forEach((c) => columns.add(c)),
    );
    buildDeliveryStepConditionConfig(cmmInputUniqueValues).fields.forEach(
      (field) => columns.add(field.value),
    );
    return buildSummaryWhenConfig(Array.from(columns));
  }, [sourceColumns, rules, cmmInputUniqueValues]);

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-primaryGray-16 bg-white p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold text-primaryGray-1">
            Summary Resolution
          </div>
          <ToggleSwitch
            id="text-notes-summary-enabled"
            defaultChecked={enabled}
            onChangeEmit={() => onEnabledChange(!enabled)}
          />
        </div>
        <Button
          buttonType="primary"
          size="medium"
          disabled={false}
          onClick={onAddRule}
          children="Add New Rule"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput
          key={`summary-field-${resetKey}`}
          id="text-notes-summary-field"
          label="Summary field name"
          defaultValue={fieldName}
          onChange={(data: { value: string }) => onFieldNameChange(data.value)}
        />
        <div className="flex flex-col">
          <div className="mb-1 text-tiny font-medium text-primaryGray-3">
            Source columns
          </div>
          <MultiSelectWithTag
            key={`source-columns-${resetKey}`}
            options={sourceColumnOptions}
            value={sourceColumns}
            onChange={onSourceColumnsChange}
            placeholder="Select columns..."
            dropdownWidth="14rem"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {rules.map((rule) => (
          <div
            key={rule.id}
            className="relative flex flex-col gap-3 rounded-lg border border-primaryGray-14 p-4"
          >
            <button
              type="button"
              className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded p-1 hover:bg-primaryGray-16"
              onClick={() => onRemoveRule(rule.id)}
              aria-label="Remove rule"
            >
              <Delete width={16} height={16} />
            </button>

            <div className="grid grid-cols-1 gap-4 pr-8 md:grid-cols-2">
              <TextInput
                key={`rule-id-${rule.id}-${resetKey}`}
                id={`text-notes-rule-id-${rule.id}`}
                label="Rule ID"
                defaultValue={rule.ruleId}
                onChange={(data: { value: string }) =>
                  onRuleChange(rule.id, { ruleId: data.value })
                }
              />
              <TextInput
                key={`rule-template-${rule.id}-${resetKey}`}
                id={`text-notes-rule-template-${rule.id}`}
                label="Value template"
                defaultValue={rule.valueTemplate}
                onChange={(data: { value: string }) =>
                  onRuleChange(rule.id, { valueTemplate: data.value })
                }
              />
            </div>

            <QueryBuilder
              key={`when-${rule.id}-${resetKey}`}
              value={rule.when}
              onChange={(when) => onWhenChange(rule.id, when)}
              config={whenConfig}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SummaryResolutionCard;
